import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from './supabaseClient';

const SellerAuth = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login', 'signup', 'forgot'
  const [form, setForm] = useState({ email: '', password: '', business_name: '', confirm_password: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Auto-redirect if already logged in
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate('/seller/dashboard');
      } else {
        setLoading(false);
      }
    };
    checkSession();
  }, [navigate]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleLogin = async () => {
    if (!form.email || !form.password) { setError('Please enter your email and password.'); return; }
    setLoading(true); setError(null);
    const { error } = await supabase.auth.signInWithPassword({ email: form.email, password: form.password });
    if (error) { setError(error.message); setLoading(false); }
    else { navigate('/seller/dashboard'); }
  };

  const handleSignup = async () => {
    if (!form.email || !form.password || !form.business_name) { setError('Please fill in all fields.'); return; }
    if (form.password !== form.confirm_password) { setError('Passwords do not match.'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true); setError(null);

    const { data, error } = await supabase.auth.signUp({ email: form.email, password: form.password });
    if (error) { setError(error.message); setLoading(false); return; }

    if (data.user) {
      await supabase.from('sellers').upsert({
        id: data.user.id,
        business_name: form.business_name,
      }, { onConflict: 'id' });
    }

    setMessage('Account created! Please check your email to confirm your account, then log in.');
    setMode('login');
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!form.email) { setError('Please enter your email address above.'); return; }
    setLoading(true); setError(null);
    const { error } = await supabase.auth.resetPasswordForEmail(form.email, {
      redirectTo: 'https://trueplantvalue.com/reset-password',
    });
    if (error) { setError(error.message); }
    else { setMessage('Password reset email sent! Check your inbox.'); setMode('login'); }
    setLoading(false);
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px', border: '2px solid #c4a882',
    borderRadius: '12px', fontSize: '15px', background: '#ffffff',
    color: '#1e2d1f', outline: 'none', fontFamily: 'Lato, sans-serif',
    boxSizing: 'border-box', marginBottom: '16px',
  };

  const labelStyle = {
    display: 'block', fontSize: '10px', letterSpacing: '2px',
    textTransform: 'uppercase', color: '#4a7c59', fontWeight: 700,
    marginBottom: '6px', fontFamily: 'Lato, sans-serif',
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '80px', fontFamily: 'Lato, sans-serif', color: '#4a7c59' }}>
      Loading...
    </div>
  );

  return (
    <div style={{ maxWidth: '440px', margin: '60px auto', padding: '0 16px 80px' }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌿</div>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '32px', color: '#1e3d20', marginBottom: '8px' }}>
          {mode === 'login' ? 'Member Login' : mode === 'forgot' ? 'Reset Password' : 'Create Member Account'}
        </h1>
        <p style={{ color: '#4a5e4b', fontFamily: 'Lato, sans-serif', fontSize: '14px' }}>
          {mode === 'login' ? 'Access your plant inventory and seller dashboard'
            : mode === 'forgot' ? 'Enter your email and we will send a reset link'
            : 'Join free and start tracking your plant inventory'}
        </p>
      </div>

      <div style={{ background: '#ffffff', borderRadius: '20px', padding: '32px', boxShadow: '0 2px 16px rgba(44,74,46,0.1)', border: '1px solid rgba(143,185,154,0.3)' }}>

        {message && (
          <div style={{ background: '#f0f7f1', border: '1px solid #4a7c59', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px' }}>
            <p style={{ color: '#2c4a2e', fontFamily: 'Lato, sans-serif', fontSize: '14px', margin: 0 }}>✅ {message}</p>
          </div>
        )}

        {/* Business name — signup only */}
        {mode === 'signup' && (
          <div>
            <label style={labelStyle}>Business / Shop Name *</label>
            <input name="business_name" value={form.business_name} onChange={handleChange}
              placeholder="e.g. My Plant Shop" style={inputStyle} autoComplete="organization" />
          </div>
        )}

        {/* Email */}
        <div>
          <label style={labelStyle}>Email *</label>
          <input name="email" type="email" value={form.email} onChange={handleChange}
            placeholder="your@email.com" style={inputStyle} autoComplete="email"
            onKeyDown={(e) => e.key === 'Enter' && mode === 'login' && handleLogin()} />
        </div>

        {/* Password — login and signup only */}
        {mode !== 'forgot' && (
          <div>
            <label style={labelStyle}>Password *</label>
            <input name="password" type="password" value={form.password} onChange={handleChange}
              placeholder="••••••••" style={{ ...inputStyle, marginBottom: mode === 'login' ? '4px' : '16px' }}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              onKeyDown={(e) => e.key === 'Enter' && mode === 'login' && handleLogin()} />
          </div>
        )}

        {/* Forgot password link — login only, right below password */}
        {mode === 'login' && (
          <div style={{ textAlign: 'right', marginBottom: '16px' }}>
            <button onClick={() => { setMode('forgot'); setError(null); setMessage(null); }}
              style={{ background: 'transparent', border: 'none', color: '#8b9e8c', fontFamily: 'Lato, sans-serif', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
              Forgot password?
            </button>
          </div>
        )}

        {/* Confirm password — signup only */}
        {mode === 'signup' && (
          <div>
            <label style={labelStyle}>Confirm Password *</label>
            <input name="confirm_password" type="password" value={form.confirm_password} onChange={handleChange}
              placeholder="••••••••" style={inputStyle} autoComplete="new-password" />
          </div>
        )}

        {error && (
          <p style={{ color: '#c0392b', fontFamily: 'Lato, sans-serif', fontSize: '13px', marginBottom: '16px', padding: '10px', background: '#fff5f5', borderRadius: '8px', border: '1px solid #c0392b' }}>
            {error}
          </p>
        )}

        <button
          onClick={mode === 'login' ? handleLogin : mode === 'forgot' ? handleForgotPassword : handleSignup}
          disabled={loading}
          style={{ width: '100%', padding: '14px', background: loading ? '#8fb99a' : 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Lato, sans-serif', marginBottom: '20px' }}>
          {loading ? 'Please wait...' : mode === 'login' ? 'Log In' : mode === 'forgot' ? 'Send Reset Email' : 'Create Account'}
        </button>

        <div style={{ textAlign: 'center' }}>
          {mode === 'forgot' ? (
            <>
              <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b' }}>Remember your password? </span>
              <button onClick={() => { setMode('login'); setError(null); setMessage(null); }}
                style={{ background: 'transparent', border: 'none', color: '#4a7c59', fontFamily: 'Lato, sans-serif', fontSize: '13px', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                Log in
              </button>
            </>
          ) : mode === 'login' ? (
            <>
              <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b' }}>Don't have an account? </span>
              <button onClick={() => { setMode('signup'); setError(null); setMessage(null); }}
                style={{ background: 'transparent', border: 'none', color: '#4a7c59', fontFamily: 'Lato, sans-serif', fontSize: '13px', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                Sign up free
              </button>
            </>
          ) : (
            <>
              <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b' }}>Already have an account? </span>
              <button onClick={() => { setMode('login'); setError(null); setMessage(null); }}
                style={{ background: 'transparent', border: 'none', color: '#4a7c59', fontFamily: 'Lato, sans-serif', fontSize: '13px', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                Log in
              </button>
            </>
          )}
        </div>
      </div>

      <p style={{ textAlign: 'center', fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#8b9e8c', marginTop: '24px' }}>
        Member accounts are free. Your inventory is private to you.
      </p>
    </div>
  );
};

export default SellerAuth;